import React from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Formik } from 'formik'
import * as Yup from 'yup'

import Api from 'services/Api'
import { Button, Form, Input } from 'components/Forms'
import { createNotification } from 'store/actions/notifications'
import { Alert } from 'ui'
import { t } from 'translations'

const EditPasswordForm = ({ finishChanges, preventCancel }) => {
  const dispatch = useDispatch()
  const _id = useSelector(state => state.auth.decodedToken.uid)
  const { customPasswordValidation, customPasswordValidationMsg, customPasswordValidationRegex } = useSelector(state => state.settings)

  const validation = !customPasswordValidation
    ? [new RegExp('^.{8,64}$'), 'employee.myProfile.PASSWORD_REQUIREMENT']
    : [new RegExp(customPasswordValidationRegex), customPasswordValidationMsg]

  // Form validation
  const EditPasswordShape = {
    password: Yup.string()
      .matches(...validation)
      .required('employee.myProfile.MANDATORY_PASSWORD'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password')], 'employee.myProfile.CONFIRMATION_FIELD_MUST_MATCH')
      .required('employee.myProfile.MANDATORY_CONFIRM_PASSWORD'),
    actualPassword: Yup.string().required('employee.myProfile.MANDATORY_ACTUAL_PASSWORD')
  }

  const onSubmit = async (body, actions) => {
    const { message, success } = await Api.put('users/password', { ...body, _id })
    if (!success) {
      actions.setSubmitting(false)
      actions.setStatus({ type: 'danger', msg: `employee.myProfile.${message}` })
    } else {
      dispatch(createNotification({ title: t(`components.notification.PASSWORD_UPDATED`), description: ``, type: 'success' }))
      actions.resetForm({})
      // Remove the edit form
      finishChanges()
    }
  }

  // Function to validate that the password and its confirmation are identical
  const shouldSetError = (values, errors) => {
    const bothFieldHaveValues = values.password && values.confirmPassword
    const fieldsAreNotIdentical = values.password !== values.confirmPassword
    const errorsAlreadySet = errors['password'] || errors['confirmPassword']

    return bothFieldHaveValues && fieldsAreNotIdentical && !errorsAlreadySet
  }

  return (
    <Formik
      validationSchema={Yup.object().shape(EditPasswordShape)}
      enableReinitialize={true}
      initialValues={{ password: '', confirmPassword: '', actualPassword: '' }}
      onSubmit={onSubmit}
    >
      {({ status, isSubmitting, dirty, resetForm, setFieldError, setStatus, values, errors }) => {
        // Validates that both the password and its confirmation are the same
        if (shouldSetError(values, errors)) {
          setFieldError('password', 'employee.myProfile.CONFIRMATION_FIELD_MUST_MATCH')
          setFieldError('confirmPassword', 'employee.myProfile.CONFIRMATION_FIELD_MUST_MATCH')
        }

        return (
          <Form resetForm={resetForm} dirty={!!dirty && !isSubmitting} leavingAction={finishChanges}>
            {({ leavingButton }) => (
              <>
                {/* Error message */}
                <Alert variant={status?.type} onClose={() => setStatus()} show={!!status?.msg}>
                  {status?.msg}
                </Alert>
                <Input module="employee.myProfile" required name="actualPassword" type="password" autoComplete="current-password" />

                <Input
                  module="employee.myProfile"
                  required
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  autoCorrect="off"
                  autoCapitalize="none"
                  customDescription={customPasswordValidation ? customPasswordValidationMsg : null}
                />
                <Input
                  module="employee.myProfile"
                  required
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  autoCorrect="off"
                  autoCapitalize="none"
                />

                <Button type="submit" isSubmitting={isSubmitting} text="employee.attendanceForm.SUBMIT" />
                {preventCancel ? null : leavingButton}
              </>
            )}
          </Form>
        )
      }}
    </Formik>
  )
}

export default EditPasswordForm
